import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageCircle,
  Bookmark,
  ThumbsUp,
  MoreHorizontal,
  Send,
  Trash2,
  Flag,
  UserX,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api, { apiError } from "@/lib/api";
import { EP } from "@/lib/endpoints";
import { toast } from "sonner";
import DifferentViewSheet from "@/components/DifferentViewSheet";
import ReportModal from "@/components/ReportModal";
import UserAvatar from "@/components/UserAvatar";
import { OpinionBubbleIcon } from "@/components/OpinionBubble";
import ImageViewerModal from "@/components/ImageViewerModal";
import { useAuth } from "@/context/AuthContext";
import { extractPostImageUrls } from "@/lib/postMedia";

/**
 * PostCard — 1:1 web port of iOS `mobile/src/components/PostCard.tsx`.
 *
 * Instagram-style card for both post types.
 *
 *   Standard  → photo body · thumbs-up (like) · comment · share · save · caption
 *   Opinion   → the photo area is replaced by the written opinion in a
 *               subtle outlined box (340px tall); thumbs-up = Agree, the
 *               comment bubble (with "+ −" inside) opens the Different
 *               Views sheet.  Both cards share the same body size.
 *
 * The action row is rendered as: thumbs-up · comment · share · [spacer] · bookmark
 * (never a repost — iOS spec removed reposts from the surface).
 *
 * The card is full-bleed: media runs edge-to-edge, header/actions/caption
 * carry their own horizontal padding.
 */

const RAINBOW =
  "linear-gradient(90deg, #FFD600, #FF7A00, #FF0069, #D300C5, #7638FA)";

// Body heights — opinion outlined box stays fixed at 340px per iOS spec.
// The standard-photo BOX now uses an aspect ratio (195:220 ≈ 0.886) instead
// of a fixed height so the crop behaves like iOS on any container width.
const BODY_HEIGHT = 340; // opinion outlined box height

function relTime(input) {
  if (!input) return "";
  const d = typeof input === "number" ? new Date(input) : new Date(input);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (Number.isNaN(diff)) return "";
  if (diff < 60) return "now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function num(n) {
  return n == null ? 0 : n;
}

// The rainbow "OPINION" pill next to the author name on opinion cards.
function OpinionTag() {
  return (
    <span
      className="inline-flex items-center rounded-[7px] px-[7px] py-[2.5px] overflow-hidden text-white text-[10px] font-extrabold tracking-[0.4px] leading-[13px]"
      style={{ background: RAINBOW }}
    >
      OPINION
    </span>
  );
}

// PostMedia — iOS spec 22 §2 exact proportions.  The iOS card is 390pt
// wide × 440pt tall for a SINGLE photo, giving an aspect of 195:220
// (≈0.886, portrait-leaning, close to Instagram's 4:5).  On the web the
// card can render wider than 390px, so we lock the *aspect-ratio* — that
// preserves the iOS proportions at every viewport width instead of
// aggressively cropping a portrait source into a wide landscape box.
//
// Multi-image grid uses the same 195:220 ratio on each 49.8%-wide tile,
// which mirrors iOS `width: 49.8%, height: MEDIA_HEIGHT/2`.
function PostMedia({ urls, onImageClick, testIdPrefix = "post-media" }) {
  if (!urls || urls.length === 0) return null;
  if (urls.length === 1) {
    return (
      <button
        type="button"
        onClick={() => onImageClick?.(0)}
        className="block w-full bg-secondary overflow-hidden"
        style={{ aspectRatio: "195 / 220" }}
        data-testid={`${testIdPrefix}-0`}
      >
        <img
          src={urls[0]}
          alt=""
          className="w-full h-full object-cover"
          loading="lazy"
          draggable={false}
        />
      </button>
    );
  }
  return (
    <div className="w-full flex flex-wrap gap-[0.4%]">
      {urls.slice(0, 4).map((u, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onImageClick?.(i)}
          className="bg-secondary overflow-hidden"
          style={{ width: "49.8%", aspectRatio: "195 / 220" }}
          data-testid={`${testIdPrefix}-${i}`}
        >
          <img
            src={u}
            alt=""
            className="w-full h-full object-cover"
            loading="lazy"
            draggable={false}
          />
        </button>
      ))}
    </div>
  );
}

export default function PostCard({ post, onDeleted, inDetail = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(post);
  const [reportOpen, setReportOpen] = useState(false);
  const [dvOpen, setDvOpen] = useState(false);
  const [viewer, setViewer] = useState({ open: false, index: 0 });

  const rawAuthor = p.author || p.user || p._user || p.author_info || p.creator || null;
  const flatAuthor =
    !rawAuthor && (p.author_username || p.author_display_name)
      ? {
          id: p.author_id,
          user_id: p.author_user_id ?? p.author_id,
          username: p.author_username,
          display_name: p.author_display_name,
          profile_photo_url: p.author_profile_photo_url,
          avatar_url: p.author_avatar_url,
          is_verified: p.author_is_verified,
        }
      : null;
  const isOwnPost =
    !!user &&
    (p.user_id === user.id ||
      rawAuthor?.id === user.id ||
      rawAuthor?.user_id === user.id ||
      flatAuthor?.id === user.id);
  const author =
    rawAuthor ||
    flatAuthor ||
    (isOwnPost
      ? user
      : {
          id: p.user_id,
          user_id: p.user_id,
          username: p.user_id ? `user${p.user_id}` : "user",
          display_name: p.user_id ? `User ${p.user_id}` : "User",
        });
  const authorName = author.display_name || author.name || author.username || "User";
  const authorUsername = author.username || "user";
  const isOwner =
    user &&
    (author.id === user.id ||
      author.user_id === user.id ||
      author.username === user.username);

  const rawPostType = p.post_type || p.Post_type || "";
  const isOpinion =
    String(rawPostType).toLowerCase() === "opinion" ||
    String(p.type || "").toLowerCase() === "opinion" ||
    p.is_opinion === true;

  const content = p.content || p.text || "";
  const created = p.created_at || p.createdAt || p.timestamp;

  const agreed = !!p.viewer_has_agreed || !!p.has_agreed || !!p.i_agreed;
  const liked = !!p.viewer_has_liked || !!p.has_liked;
  const saved = !!p.viewer_saved || !!p.is_saved;

  const agreeCount = num(p.agree_count ?? p.agrees);
  const likeCount = num(p.like_count ?? p.likes_count);
  const replyCount = num(p.reply_count ?? p.replies_count ?? p.comments_count);
  const dvCount = num(p.different_view_count ?? p.different_views_count);

  // Post images resolved to absolute URLs (handles every Xano shape).
  const imageUrls = extractPostImageUrls(p);

  const openDetail = () => {
    if (inDetail) return;
    navigate(`/post/${p.id ?? p.post_id}`);
  };
  const openImage = (index) => setViewer({ open: true, index });

  // Opinion-post AGREE — thumbs-up on opinion cards.
  const submitAgree = async () => {
    const next = !agreed;
    setP((prev) => ({
      ...prev,
      viewer_has_agreed: next,
      agree_count: (prev.agree_count ?? agreeCount) + (next ? 1 : -1),
    }));
    try {
      if (next) await api.post(EP.agreePost(p.id), {});
      else await api.delete(EP.unagreePost(p.id));
    } catch (e) {
      setP((prev) => ({
        ...prev,
        viewer_has_agreed: !next,
        agree_count: (prev.agree_count ?? agreeCount) + (next ? -1 : 1),
      }));
      toast.error(apiError(e));
    }
  };

  // Standard-post LIKE — thumbs-up on standard cards.
  const submitLike = async () => {
    const next = !liked;
    setP((prev) => ({
      ...prev,
      viewer_has_liked: next,
      like_count: (prev.like_count ?? likeCount) + (next ? 1 : -1),
    }));
    try {
      if (next) await api.post(EP.likePost(p.id), {});
      else await api.delete(EP.unlikePost(p.id));
    } catch (e) {
      setP((prev) => ({
        ...prev,
        viewer_has_liked: !next,
        like_count: (prev.like_count ?? likeCount) + (next ? -1 : 1),
      }));
      toast.error(apiError(e));
    }
  };

  const toggleSave = async () => {
    const next = !saved;
    setP((prev) => ({ ...prev, viewer_saved: next }));
    try {
      if (next) {
        await api.post(EP.savePost(p.id), {});
        toast.success("Post Saved");
      } else {
        await api.delete(EP.unsavePost(p.id));
      }
    } catch (e) {
      setP((prev) => ({ ...prev, viewer_saved: !next }));
      toast.error(apiError(e));
    }
  };

  const doDelete = async () => {
    if (!window.confirm("Delete this post? This cannot be undone.")) return;
    try {
      await api.delete(EP.post(p.id));
      toast.success("Post deleted");
      onDeleted?.(p.id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const blockAuthor = async () => {
    const uid = p.user_id ?? author?.user_id ?? author?.id;
    if (!uid && !author?.username) {
      toast.error("Couldn't identify this user.");
      return;
    }
    if (!window.confirm(`Block @${authorUsername}?`)) return;
    try {
      await api.post(EP.blockUser(uid), { username: author?.username });
      toast.success(`@${authorUsername} blocked`);
      onDeleted?.(p.id);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const sharePost = async () => {
    const message = [content, `— ${authorName} (@${authorUsername}) on BrontoY`]
      .filter(Boolean)
      .join("\n\n");
    if (navigator.share) {
      try {
        await navigator.share({ text: message, url: `${window.location.origin}/post/${p.id}` });
        return;
      } catch {
        /* user dismissed */
      }
    }
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/post/${p.id}`);
      toast.success("Link copied.");
    } catch {
      toast.error("Couldn't copy link.");
    }
  };

  const reactionCount = isOpinion ? agreeCount : likeCount;
  const reactionLabel = isOpinion
    ? `${agreeCount} ${agreeCount === 1 ? "agree" : "agrees"}`
    : `${likeCount} ${likeCount === 1 ? "like" : "likes"}`;

  return (
    <article
      className="pb-[18px] border-b border-border bg-card"
      data-testid={`post-${p.id}`}
    >
      {/* Header — avatar · name (+ OPINION tag) · time · three-dot menu */}
      <div className="px-3 py-[10px] flex items-center gap-[10px]">
        <Link to={`/${authorUsername}`} data-testid={`post-avatar-${p.id}`}>
          <UserAvatar user={author} size="md" />
        </Link>
        <div className="flex-1 min-w-0 flex items-center gap-[6px] flex-wrap">
          <Link
            to={`/${authorUsername}`}
            className="font-grand-hotel text-foreground hover:underline underline-offset-4"
            style={{ fontSize: 19, lineHeight: "24px" }}
            data-testid={`post-author-${p.id}`}
          >
            {authorName}
          </Link>
          {isOpinion && <OpinionTag />}
          <span className="text-muted-foreground text-[13px] leading-[18px]">
            · {relTime(created)}
          </span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="pl-2 -mr-1 text-muted-foreground hover:opacity-80"
              data-testid={`post-menu-${p.id}`}
              aria-label="More"
            >
              <MoreHorizontal size={20} strokeWidth={1.8} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {isOwner ? (
              <DropdownMenuItem
                onClick={doDelete}
                className="text-destructive focus:text-destructive"
                data-testid={`post-menu-delete-${p.id}`}
              >
                <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Delete post
              </DropdownMenuItem>
            ) : (
              <>
                <DropdownMenuItem
                  onClick={() => setReportOpen(true)}
                  data-testid={`post-menu-report-${p.id}`}
                >
                  <Flag className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Report post
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={blockAuthor}
                  className="text-destructive focus:text-destructive"
                  data-testid={`post-menu-block-${p.id}`}
                >
                  <UserX className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Block user
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Body — photo for standard posts; opinion text in an outlined
          box (340px tall) for opinion posts. */}
      {isOpinion ? (
        <button
          type="button"
          onClick={() => setDvOpen(true)}
          className="block w-full px-3"
          data-testid={`opinion-body-${p.id}`}
        >
          <div
            className="w-full rounded-2xl border flex items-center justify-center px-5 opinion-box"
            style={{ height: BODY_HEIGHT }}
          >
            <p
              className="text-center text-foreground line-clamp-9"
              style={{
                fontSize: 21,
                lineHeight: "30px",
                fontWeight: 600,
                letterSpacing: "-0.2px",
                display: "-webkit-box",
                WebkitLineClamp: 9,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {content}
            </p>
          </div>
        </button>
      ) : imageUrls.length > 0 ? (
        // Tap image → fullscreen viewer (works from both feed and detail).
        // Post detail can still be reached via the comment button or the
        // caption below the image.
        <PostMedia
          urls={imageUrls}
          onImageClick={openImage}
          testIdPrefix={`post-media-${p.id}`}
        />
      ) : null}

      {/* Action row — thumbs-up · comment · share · [spacer] · bookmark */}
      <div className="flex items-center gap-[18px] px-3 pt-[10px] pb-2">
        <ActionBtn
          onClick={isOpinion ? submitAgree : submitLike}
          testId={`${isOpinion ? "agree" : "like"}-${p.id}`}
          aria-label={isOpinion ? (agreed ? "Un-agree" : "Agree") : liked ? "Unlike" : "Like"}
        >
          {(isOpinion ? agreed : liked) ? (
            <ThumbsUp
              size={24}
              strokeWidth={1.8}
              className="rainbow-thumb"
              style={{
                fill: "url(#thumbGradient)",
                stroke: "url(#thumbGradient)",
              }}
            />
          ) : (
            <ThumbsUp
              size={24}
              strokeWidth={1.8}
              className="text-foreground"
              fill="transparent"
            />
          )}
        </ActionBtn>

        <ActionBtn
          onClick={isOpinion ? () => setDvOpen(true) : openDetail}
          testId={`${isOpinion ? "dv" : "comment"}-${p.id}`}
          aria-label={isOpinion ? "Different view" : "Comments"}
        >
          {isOpinion ? (
            <OpinionBubbleIcon size={24} className="text-foreground" />
          ) : (
            <MessageCircle size={24} strokeWidth={1.8} className="text-foreground" />
          )}
        </ActionBtn>

        <ActionBtn
          onClick={sharePost}
          testId={`share-${p.id}`}
          aria-label="Share"
        >
          <Send size={23} strokeWidth={1.8} className="text-foreground" />
        </ActionBtn>

        <div className="flex-1" />

        <ActionBtn
          onClick={toggleSave}
          testId={`save-${p.id}`}
          aria-label={saved ? "Unsave" : "Save"}
        >
          <Bookmark
            size={24}
            strokeWidth={1.8}
            style={{
              color: saved ? "#F5A623" : "hsl(var(--foreground))",
              fill: saved ? "#F5A623" : "transparent",
            }}
          />
        </ActionBtn>
      </div>

      {/* Rainbow-gradient SVG mask used by the "liked/agreed" thumbs-up */}
      <svg width="0" height="0" style={{ position: "absolute" }}>
        <defs>
          <linearGradient id="thumbGradient" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#FFD600" />
            <stop offset="25%" stopColor="#FF7A00" />
            <stop offset="50%" stopColor="#FF0069" />
            <stop offset="75%" stopColor="#D300C5" />
            <stop offset="100%" stopColor="#7638FA" />
          </linearGradient>
        </defs>
      </svg>

      {/* Counts + caption below the action row */}
      <div className="px-3">
        {reactionCount > 0 || replyCount > 0 || dvCount > 0 ? (
          <p className="text-foreground text-[14px] leading-[19px] font-semibold mb-[3px]">
            {reactionLabel}
            {isOpinion && dvCount > 0
              ? `  ·  ${dvCount} different ${dvCount === 1 ? "view" : "views"}`
              : null}
            {!isOpinion && replyCount > 0
              ? `  ·  ${replyCount} ${replyCount === 1 ? "comment" : "comments"}`
              : null}
          </p>
        ) : null}
        {!isOpinion && content ? (
          <button
            type="button"
            onClick={openDetail}
            disabled={!!inDetail}
            className="text-left w-full"
          >
            <p
              className={`text-foreground text-[14.5px] leading-5 ${
                inDetail ? "" : "line-clamp-3"
              }`}
              style={
                inDetail
                  ? undefined
                  : {
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }
              }
            >
              <span className="font-semibold">{authorName}</span>
              <span> - {content}</span>
            </p>
          </button>
        ) : null}
      </div>

      <DifferentViewSheet
        open={dvOpen}
        onClose={() => setDvOpen(false)}
        postId={p.id}
        onCountChange={(delta) =>
          setP((prev) => ({
            ...prev,
            different_view_count: Math.max(
              0,
              (prev.different_view_count ?? dvCount) + delta
            ),
          }))
        }
      />

      <ReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType="post"
        targetId={p.id}
        targetLabel={content}
      />

      <ImageViewerModal
        urls={imageUrls}
        open={viewer.open}
        initialIndex={viewer.index}
        onClose={() => setViewer({ open: false, index: 0 })}
      />
    </article>
  );
}

function ActionBtn({ onClick, testId, children, ...rest }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className="flex items-center justify-center transition-transform active:scale-90"
      {...rest}
    >
      {children}
    </button>
  );
}
